<template>
  <section>
    <!-- Barre de recherche en haut -->
    <section class="sticky top-0 z-40 bg-white shadow-sm py-4 px-6 md:px-20 lg:px-28">
      <form
        @submit.prevent="handleSearch"
        class="bg-white rounded-xl flex flex-col md:flex-row items-center gap-4 md:gap-6 p-4 relative"
        role="search"
      >
        <div class="flex-1 w-full">
          <label for="query" class="sr-only">Prestation ou salon</label>
          <input
            v-model="filters.query"
            id="query"
            type="text"
            placeholder="Nom du salon, prestation (coupe, soin...)"
            class="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>

        <div class="flex-1 w-full relative">
          <label for="location" class="sr-only">Lieu</label>
          <input
            v-model="filters.location"
            id="location"
            type="text"
            placeholder="Ville ou adresse"
            @input="onLocationInput"
            @focus="showSuggestions = true"
            @blur="hideSuggestions"
            autocomplete="off"
            class="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-black"
          />

          <!-- Dropdown suggestions -->
          <ul
            v-if="showSuggestions && suggestions.length"
            class="absolute z-50 bg-white border border-gray-300 rounded-md mt-1 w-full max-h-60 overflow-auto shadow-lg"
          >
            <li
              v-for="(suggestion, index) in suggestions"
              :key="index"
              @mousedown.prevent="selectSuggestion(suggestion.description)"
              class="cursor-pointer px-4 py-2 hover:bg-gray-100"
            >
              {{ suggestion.description }}
            </li>
          </ul>
        </div>

        <button
          type="submit"
          class="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors w-full md:w-auto"
        >
          Rechercher
        </button>
      </form>
    </section>

    <!-- Résultats -->
    <section class="py-16 bg-gray-50">
      <div class="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div
          v-if="salons.length"
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          <SalonCard
            v-for="salon in salons"
            :key="salon._id"
            :id="salon._id"
            :logo="salon.logo"
            :nom="salon.nom_societe"
            :horaire="formatHoraireDuJour(salon.horaires)"
            :distance="salon.distance_km"
          />
        </div>
        <div v-else class="text-center text-gray-600 text-lg py-24">
          Aucun vétérinaire trouvé. Essayez avec d'autres critères.
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import SalonCard from './SalonCard.vue'

export default {
  components: { SalonCard },
  data() {
    return {
      filters: {
        query: '',
        location: ''
      },
      salons: [],
      suggestions: [],
      showSuggestions: false,
      debounceTimer: null
    }
  },
  watch: {
    filters: {
      handler() {
        this.fetchResults()
      },
      deep: true
    }
  },
  mounted() {
    this.filters.query = this.$route.query.query || ''
    this.filters.location = this.$route.query.location || ''
    this.fetchResults()
  },
  methods: {
    formatHoraireDuJour(horaires) {
      if (!horaires) return 'Non renseigné'

      const jours = [
        'dimanche', 'lundi', 'mardi', 'mercredi',
        'jeudi', 'vendredi', 'samedi'
      ]
      const today = jours[new Date().getDay()]
      const horairesDuJour = horaires[today]

      if (!horairesDuJour || horairesDuJour.length === 0) {
        return 'Fermé'
      }

      return horairesDuJour
        .map(creneau => `${creneau.debut} - ${creneau.fin}`)
        .join(' / ')
    },
    async fetchResults() {
      try {
        const params = new URLSearchParams()
        if (this.filters.query.trim()) params.append('query', this.filters.query.trim())
        if (this.filters.location.trim()) params.append('location', this.filters.location.trim())

        const response = await fetch(`https://b53c7d5edf32.ngrok-free.app/api/search?${params.toString()}`)

        if (!response.ok) throw new Error('Erreur lors de la récupération des résultats')
        this.salons = await response.json()
      } catch (e) {
        console.error(e)
        this.salons = []
      }
    },
    handleSearch() {
      this.$router.push({ path: '/search', query: { ...this.filters } })
      this.showSuggestions = false
    },
    async onLocationInput() {
      clearTimeout(this.debounceTimer)
      if (this.filters.location.trim().length < 2) {
        this.suggestions = []
        return
      }
      // Debounce 300ms pour éviter trop de requêtes
      this.debounceTimer = setTimeout(async () => {
        try {
          const response = await fetch(`https://b53c7d5edf32.ngrok-free.app/api/google-cities-autocomplete?input=${encodeURIComponent(this.filters.location.trim())}`)
          if (!response.ok) throw new Error('Erreur autocomplete')
          const data = await response.json()
          // Google Places API renvoie un tableau predictions
          this.suggestions = data.predictions || []
        } catch (e) {
          console.error(e)
          this.suggestions = []
        }
      }, 300)
    },
    selectSuggestion(description) {
      this.filters.location = description
      this.suggestions = []
      this.showSuggestions = false
    },
    hideSuggestions() {
      // On cache le dropdown après un petit délai pour laisser le click s'enregistrer
      setTimeout(() => {
        this.showSuggestions = false
      }, 200)
    }
  }
}
</script>
